import { forwardRef, useEffect, useImperativeHandle } from 'react';

import { StudentAccessProduct, StudentApi, StudentContentAccessDetail } from 'apis/StudentApi';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { TransactionApi } from 'apis/TransactionApi';
import { useNotification } from 'app/notifications';
import { Modal } from 'components/modal_manager';
import { Alert } from 'react-bootstrap';
import { useSuperState } from 'hooks';

type ClassroomAccessRecord = {
    id: number;
    detail: StudentContentAccessDetail;
};

export type EditClassroomCodeDialogHandle = {
    hide: () => void;
    show: (groupId: number, studentId: number, control: string | undefined, groupName?: string) => void;
    visible: boolean;
};

type EditClassroomCodeDialogProps = {
    onSaved: () => void;
};

export const EditClassroomCodeDialog = forwardRef<EditClassroomCodeDialogHandle, EditClassroomCodeDialogProps>((props, forwardedRef) => {

    const { onSaved } = props;

    const { formatMessage } = useIntl();
    const { catchWithToast, onErrorToast, onSuccessToast } = useNotification();

    const [state, setState, resetState] = useSuperState({
        accessRecords: [] as ClassroomAccessRecord[],
        active: false,
        booksRemaining: '',
        cloudAccessEnabled: false,
        cloudAccessRemaining: '',
        coursesRemaining: '',
        expirationDate: '',
        groupId: null as number | null,
        groupName: '',
        identifier: '',
        loading: false,
        originalIdentifier: '',
        productId: null as number | null,
        saving: false,
        studentId: null as number | null,
        testsRemaining: '',
        visible: false
    });

    const numberToInput = (value: number | null): string => (value !== null && value !== undefined ? String(value) : '');

    const inputToNumber = (value: string): number | null => {

        const trimmed = value.trim();
        if (trimmed === '') {

            return null;

        }
        const parsed = Number(trimmed);
        return Number.isNaN(parsed) ? null : parsed;

    };

    const show = (groupId: number, studentId: number, control: string | undefined, groupName?: string) => {

        const identifier = control ? control.split('-')[0] : '';
        setState({
            groupId,
            groupName: groupName ?? '',
            identifier,
            originalIdentifier: identifier,
            studentId,
            visible: true
        });

    };

    const hide = () => resetState();

    const loadAccessRecords = async () => {

        if (state.groupId && state.studentId) {

            try {

                setState({ loading: true });

                const [groupTransactions, studentTransactions] = await Promise.all([
                    StudentGroupApi.GetTransactionsFromGroup(state.groupId),
                    TransactionApi.GetStudentTransactions(state.studentId)
                ]);

                const groupTransactionIds = new Set(groupTransactions.map(t => t.Id));
                let matchedAccessIds = studentTransactions
                    .filter(t => groupTransactionIds.has(t.TransactionId))
                    .map(t => t.StudentContentAccessId);

                if (matchedAccessIds.length === 0 && groupTransactions.length > 0) {

                    const assignedStudentsByTransaction = await Promise.all(
                        groupTransactions.map(transaction => TransactionApi.GetStudentsByTransactionId(transaction.Id))
                    );

                    matchedAccessIds = assignedStudentsByTransaction
                        .flatMap(students => students)
                        .filter(student => student.Id === state.studentId)
                        .map(student => student.StudentContentAccessId)
                        .filter((value, index, array) => array.indexOf(value) === index);

                }

                const details = await Promise.all(matchedAccessIds.map(id => StudentApi.GetStudentAccessDetail(id)));
                const accessRecords = matchedAccessIds.map((id, index) => ({ id, detail: details[index] }));
                const representative = accessRecords[0]?.detail;
                setState({
                    accessRecords,
                    loading: false,
                    ...(representative ? {
                        active: representative.IsActive,
                        booksRemaining: numberToInput(representative.BooksRemaining),
                        cloudAccessEnabled: representative.CloudAccessRemaining != null && representative.CloudAccessRemaining > 0,
                        cloudAccessRemaining: numberToInput(representative.CloudAccessRemaining),
                        coursesRemaining: numberToInput(representative.CoursesRemaining),
                        expirationDate: representative.ExpirationDate,
                        productId: representative.ProductID,
                        testsRemaining: numberToInput(representative.TestsRemaining)
                    } : {})
                });

            } catch (e) {

                catchWithToast(e, formatMessage({ id: 'failed_to_load_the_students_classroom_ac.b10918d7', defaultMessage: "Failed to load the student's classroom access." }));
                setState({ loading: false });

            }

        }

    };

    const handleSave = async () => {

        if (state.groupId) {

            try {

                setState({ saving: true });

                const trimmedIdentifier = state.identifier.trim();

                // Save the classroom code identifier only when it actually changed.
                if (trimmedIdentifier && trimmedIdentifier !== state.originalIdentifier.trim()) {

                    const result = await StudentGroupApi.EditStudentGroupIdentifier(state.groupId, trimmedIdentifier);
                    if (!result.Done) {

                        onErrorToast(result.Message || formatMessage({ id: 'failed_to_update_the_classroom_code.21b9bdee', defaultMessage: 'Failed to update the classroom code.' }));
                        setState({ saving: false });
                        return;

                    }

                }

                // Apply the student's uses to every access record tied to this classroom.
                for (const record of state.accessRecords) {

                    const detail = record.detail;
                    await StudentApi.UpdateStudentAccessDetail(
                        record.id,
                        state.active,
                        state.expirationDate,
                        detail.TestsRemaining !== null ? inputToNumber(state.testsRemaining) : null,
                        detail.BooksRemaining !== null ? inputToNumber(state.booksRemaining) : null,
                        detail.CoursesRemaining !== null ? inputToNumber(state.coursesRemaining) : null,
                        state.cloudAccessEnabled
                            ? (inputToNumber(state.cloudAccessRemaining) ?? 10)
                            : (detail.CloudAccessRemaining != null ? 0 : null),
                        detail.IsAssociateProduct === 1 && detail.ProductID != null && detail.ProductID !== 0 && detail.Products?.some(product => product.Id === state.productId)
                            ? state.productId
                            : null
                    );

                }

                setState({ saving: false });
                onSuccessToast(formatMessage({ id: 'successfully_updated.c1c0d6f2', defaultMessage: 'Successfully updated' }));
                onSaved();
                hide();

            } catch (e) {

                catchWithToast(e, formatMessage({ id: 'failed_to_update_the_classroom.dee64b44', defaultMessage: 'Failed to update the classroom.' }));
                setState({ saving: false });

            }

        }

    };

    useEffect(() => {

        if (state.visible && state.groupId && state.studentId) {

            loadAccessRecords();

        }

    }, [state.visible, state.groupId, state.studentId]);

    useImperativeHandle(forwardedRef, () => ({
        hide,
        show,
        visible: state.visible
    } as EditClassroomCodeDialogHandle));

    const representative = state.accessRecords[0]?.detail;
    const trimmedIdentifier = state.identifier.trim();
    const cloudAccessValue = inputToNumber(state.cloudAccessRemaining);
    const cloudAccessInvalid = state.cloudAccessEnabled
        && (cloudAccessValue === null || !Number.isInteger(cloudAccessValue) || cloudAccessValue < 0 || cloudAccessValue > 32767);
    const saveDisabled = state.saving
        || state.loading
        || trimmedIdentifier.length === 0
        || trimmedIdentifier.length > 20
        || cloudAccessInvalid;

    return (
        <Modal
            onHide={hide}
            show={state.visible}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title className="w-100 fw-bold">
                    {state.groupName || <FormattedMessage defaultMessage="Change Identifier" id="change_identifier.36bc5099" />}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                {state.loading
                    ? (
                        <div className="text-center p-5">
                            <GMetrixLoader/>
                        </div>
                    )
                    : (
                        <div className="d-flex flex-column gap-3">
                            <div className="card">
                                <div className="card-body d-flex flex-column gap-2">
                                    <h6 className="text-uppercase text-muted small fw-bold m-0">
                                        <FormattedMessage defaultMessage="Classroom Code" id="classroom_code.f81b029d" />
                                    </h6>
                                    <div className="text-muted small">
                                        <FormattedMessage defaultMessage="You may edit the Identifier on this code below." id="you_may_edit_the_identifier_on_this_code.2bc4900f" />
                                    </div>
                                    <div className="row">
                                        <div className="col-12 col-md-6">
                                            <GMetrixInput
                                                autoFocus
                                                label={formatMessage({ id: 'identifier_20_characters_or_less.13482ae8', defaultMessage: 'Identifier (20 characters or less)' })}
                                                maxLength={20}
                                                onChange={e => setState({ identifier: e.target.value })}
                                                value={state.identifier}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!representative && (
                            <Alert className="mb-0" variant="info">

                                <FormattedMessage defaultMessage="This student does not currently have an access record tied to this classroom, so there are no per-student settings to edit yet." id="this_student_does_not_currently_have_an.48246ef2" />
                            </Alert>
                            )}

                            {representative && (
                            <div className="card">
                                <div className="card-body d-flex flex-column gap-3">
                                    <h6 className="text-uppercase text-muted small fw-bold m-0">
                                        <FormattedMessage defaultMessage="Settings" id="settings.277e0df0" />
                                    </h6>

                                    <div className="d-flex flex-wrap gap-4">
                                        <GMetrixCheckInput
                                            checked={state.active}
                                            label={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })}
                                            onChange={e => setState({ active: e.target.checked })}
                                            type="checkbox"
                                        />

                                        <GMetrixCheckInput
                                            onChange={e => setState({
                                                cloudAccessEnabled: e.target.checked,
                                                cloudAccessRemaining: e.target.checked && (state.cloudAccessRemaining.trim() === '' || Number(state.cloudAccessRemaining) <= 0) ? '10' : state.cloudAccessRemaining
                                            })}
                                            checked={state.cloudAccessEnabled}
                                            label={formatMessage({ id: 'cloud_access.95fe7c27', defaultMessage: 'Cloud Access' })}
                                            type="checkbox"
                                        />
                                    </div>

                                    <div className="text-muted small">
                                        <FormattedMessage defaultMessage={'Note: these settings apply to all {count} of this student\'s access records in this classroom.'} id="note_these_settings_apply_to_all_count_o.27fbc269" values={{ count: String(state.accessRecords.length) }} />
                                    </div>

                                    <div className="row">
                                        <div className="col-12 col-md-6">
                                            <GMetrixDatePicker
                                                label={formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' })}
                                                onChange={(date: any) => setState({ expirationDate: date ? new Date(date).toISOString() : '' })}
                                                value={state.expirationDate}
                                            />
                                        </div>
                                    </div>

                                    {representative.IsAssociateProduct === 1 && representative.ProductID != null && representative.ProductID !== 0 && (
                                    <div className="row">
                                        <div className="col-12 col-md-6">
                                            <GMetrixDropdown<StudentAccessProduct>
                                                dataItemKey="Id"
                                                dataItems={representative.Products ?? []}
                                                disabled={(representative.Products?.length ?? 0) === 0}
                                                label={formatMessage({ id: 'associated_product_first_use.91a4c783', defaultMessage: 'Associated Product (First Use)' })}
                                                onChange={value => setState({ productId: (value as StudentAccessProduct)?.Id ?? null })}
                                                placeholder={formatMessage({ id: 'select_a_product.421ccca4', defaultMessage: 'Select a Product', description: 'Formulario' })}
                                                textField="Name"
                                                value={representative.Products?.find(product => product.Id === state.productId)}
                                            />
                                            <div className="small text-muted mt-1">
                                                <FormattedMessage defaultMessage="The product each student's code locked to on first test launch. Changing it moves every student in this classroom to the selected product." id="the_product_each_students_code_locked_to.1afaf3d3" />
                                            </div>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            </div>
                            )}

                            {representative && (state.cloudAccessEnabled || representative.CloudAccessRemaining != null || representative.TestsRemaining !== null || representative.BooksRemaining !== null || representative.CoursesRemaining !== null) && (
                            <div className="card">
                                <div className="card-body d-flex flex-column gap-3">
                                    <h6 className="text-uppercase text-muted small fw-bold m-0">
                                        <FormattedMessage defaultMessage="Content" id="content.e305279d" />
                                    </h6>

                                    {(state.cloudAccessEnabled || representative.CloudAccessRemaining != null) && (
                                    <div className="row">
                                        <div className="col-12">
                                            <GMetrixInput
                                                label={formatMessage({ id: 'cloud_access_available.6a38d888', defaultMessage: 'Cloud Access Available' })}
                                                min={0}
                                                onChange={e => setState({ cloudAccessRemaining: e.target.value.replace(/^0+(?=\d)/, '') })}
                                                readOnlyNoLock={!state.cloudAccessEnabled}
                                                type="number"
                                                value={state.cloudAccessEnabled ? state.cloudAccessRemaining : '0'}
                                            />
                                            {cloudAccessInvalid && (
                                            <div className="text-danger small mt-1">
                                                <FormattedMessage defaultMessage="Cloud access must be a whole number of 0 or more." id="cloud_access_must_be_a_whole_number_of_0.fd132f71" />
                                            </div>
                                            )}
                                        </div>
                                    </div>
                                    )}

                                    <div className="d-flex flex-wrap gap-3">
                                        {representative.TestsRemaining !== null && (
                                        <GMetrixInput
                                            label={formatMessage({ id: 'tests_available.9288f12e', defaultMessage: 'Tests Available' })}
                                            min={0}
                                            onChange={e => setState({ testsRemaining: e.target.value })}
                                            type="number"
                                            value={state.testsRemaining}
                                        />
                                        )}

                                        {representative.BooksRemaining !== null && (
                                        <GMetrixInput
                                            label={formatMessage({ id: 'books_available.1164fb1d', defaultMessage: 'Books Available' })}
                                            min={0}
                                            onChange={e => setState({ booksRemaining: e.target.value })}
                                            type="number"
                                            value={state.booksRemaining}
                                        />
                                        )}

                                        {representative.CoursesRemaining !== null && (
                                        <GMetrixInput
                                            label={formatMessage({ id: 'courses_available.c050e4c4', defaultMessage: 'Courses Available' })}
                                            min={0}
                                            onChange={e => setState({ coursesRemaining: e.target.value })}
                                            type="number"
                                            value={state.coursesRemaining}
                                        />
                                        )}
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <GMetrixButton
                    disabled={state.saving}
                    onClick={hide}
                    variant="outline-secondary"
                >
                    <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                </GMetrixButton>
                <GMetrixButton
                    disabled={saveDisabled}
                    onClick={handleSave}
                    variant="primary"
                >
                    <FormattedMessage defaultMessage="Save" id="save.d44082ae" />
                </GMetrixButton>
            </Modal.Footer>
        </Modal>
    );

});

EditClassroomCodeDialog.displayName = 'EditClassroomCodeDialog';
